"use client";

import { useMemo } from "react";
import BigNumber from "bignumber.js";
import {
  useWalletPortfolioContext,
  usePortfolioFiat,
} from "../../providers/wallet-portfolio-provider";
import { useCrossdPositions } from "../../queries/use-crossd-positions";
import { useCrossdPositionDetails } from "../../queries/use-crossd-position-details";
import { useTokenInfo } from "../../queries/use-token-info";
import { APP_LAUNCHER_ICONS_BASE, CROSSD_WEB } from "../../constants";
import { displayCrossdSymbol, formatFiatFromUsd } from "../../utils";
import { SectionCard } from "./SectionCard";
import { CrossdWithdrawDialog } from "./CrossdWithdrawDialog";
import { useCrossdWithdrawFlow } from "./useCrossdWithdrawFlow";
import { ExternalLinkIcon } from "../icons";
import { handleOutlinkClick } from "../../../../lib/outlink";

const SECTION_TITLE = "ONEusd Pool";

// NOTE: CDN에 `CROSS.svg`는 없다(403) — 실존 파일은 `Cross.svg`.
const FALLBACK_TOKEN_ICON =
  "https://contents.crosstoken.io/wallet/token/images/Cross.svg";

/**
 * CROSSD v3 pool 포지션 섹션. `ForgeSection`/`PointsSection` 구조를 그대로
 * 따른다 — 항목 나열은 `wp-item`, Withdraw는 `sendTransaction` 주입 여부에
 * 따라 내장 다이얼로그(`CrossdWithdrawDialog`) 또는 CROSSD 풀 웹 아웃링크로
 * 분기한다. 실수 클릭으로 트랜잭션 다이얼로그가 뜨지 않도록 Withdraw는
 * 행 전체 링크가 아니라 명시적 버튼/링크로 둔다.
 */
export function CrossdPoolSection() {
  const { env, walletAddress, onOutlink } = useWalletPortfolioContext();
  const fiat = usePortfolioFiat();
  const hasAddress = !!walletAddress;
  const { positions, isLoading } = useCrossdPositions(
    env,
    walletAddress,
    hasAddress,
  );

  const flow = useCrossdWithdrawFlow();

  // 이번 세션에서 출금 성공한 포지션은 인덱서 반영 전이라도 즉시 숨긴다.
  const visiblePositions = useMemo(
    () =>
      positions.filter(
        (p) => !p.token_id || !flow.withdrawnTokenIds.has(p.token_id),
      ),
    [positions, flow.withdrawnTokenIds],
  );

  const tokenIds = useMemo(
    () =>
      visiblePositions
        .map((p) => p.token_id)
        .filter((id): id is string => !!id),
    [visiblePositions],
  );
  const { detailMap } = useCrossdPositionDetails(
    env,
    tokenIds,
    hasAddress && tokenIds.length > 0,
  );

  const poolWebUrl = `${CROSSD_WEB[env]}/pool`;

  // 토큰 심볼/아이콘은 wallet token info API를 단일 출처로 쓴다(다른 섹션과
  // 동일). cross-defi 쪽 심볼/logo_url은 API에 없는 토큰의 폴백으로만 사용.
  // WCROSS는 crossd 관례대로 표시상 CROSS로 바꾼다 (트랜잭션은 원래 주소 유지).
  const { getTokenImage, getTokenSymbol, getNativeToken } = useTokenInfo(
    env,
    hasAddress,
  );

  // 섹션 아이콘도 info API의 ONEUSD 토큰 이미지를 쓴다 (app-launcher icons
  // CDN에는 oneusd 아이콘 없음). API 미로드/미등록 시 기존 crossd.png 폴백.
  const sectionIcon =
    getTokenImage("", "ONEUSD", { chainOnly: true }) ||
    `${APP_LAUNCHER_ICONS_BASE[env]}/crossd.png`;
  // CROSSD 풀은 전부 CROSS 체인 토큰이므로 체인 한정으로만 조회한다 —
  // 심볼 폴백이 다른 체인(BSC 등)의 동명 토큰(예: BNB 쪽 CROSS) 아이콘을
  // 잡는 것을 막는다. stg=테스트넷/prod=메인넷 chain_id는 useTokenInfo 내부의
  // CHAIN_IDS[env]가 이미 구분한다.
  const CHAIN_ONLY = { chainOnly: true } as const;
  const resolveTokenSymbol = (
    token?: { address?: string; symbol?: string },
    listSymbol?: string,
  ) => {
    const display = displayCrossdSymbol(
      getTokenSymbol(
        token?.address ?? "",
        token?.symbol ?? listSymbol,
        CHAIN_ONLY,
      ),
    );
    // WONE(wrapped native)로 판정되면 하드코딩 표기 대신 info API의
    // 네이티브 토큰 엔트리(sentinel 0x…0001) 심볼을 따른다 (stg=tONE,
    // prod=ONE). API가 유일한 출처 — 엔트리 없으면 "?" 처리.
    if (display === "ONE") {
      return getNativeToken()?.symbol ?? "?";
    }
    return display ?? "?";
  };
  const resolveTokenIcon = (
    token?: { address?: string; symbol?: string; logo_url?: string },
    listSymbol?: string,
  ) => {
    const rawSymbol = token?.symbol ?? listSymbol;
    const display = displayCrossdSymbol(rawSymbol);
    // 네이티브/WONE은 cross-defi logo_url을 신뢰하지 않고 info API의
    // 네이티브 토큰 엔트리(sentinel 0x…0001) 아이콘으로 고정한다.
    if (display === "ONE") {
      return getNativeToken()?.image || FALLBACK_TOKEN_ICON;
    }
    return (
      getTokenImage(token?.address ?? "", display, CHAIN_ONLY) ||
      token?.logo_url ||
      FALLBACK_TOKEN_ICON
    );
  };

  const targetPool = flow.target?.detail.pool;
  const targetIcon0 = resolveTokenIcon(
    targetPool?.token0,
    flow.target?.position.pool?.token0_symbol,
  );
  const targetIcon1 = resolveTokenIcon(
    targetPool?.token1,
    flow.target?.position.pool?.token1_symbol,
  );
  const targetSymbol0 = resolveTokenSymbol(
    targetPool?.token0,
    flow.target?.position.pool?.token0_symbol,
  );
  const targetSymbol1 = resolveTokenSymbol(
    targetPool?.token1,
    flow.target?.position.pool?.token1_symbol,
  );

  return (
    <>
      <SectionCard
        sectionKey="crossdPool"
        title={SECTION_TITLE}
        icon={sectionIcon}
        iconFit="contain"
        isEmpty={visiblePositions.length === 0}
        loading={isLoading}
      >
        {visiblePositions.map((position) => {
          const detail = position.token_id
            ? detailMap.get(position.token_id)
            : undefined;
          const pairLabel = `${resolveTokenSymbol(
            detail?.pool?.token0,
            position.pool?.token0_symbol,
          )}/${resolveTokenSymbol(
            detail?.pool?.token1,
            position.pool?.token1_symbol,
          )}`;
          const feeBadge = position.pool?.fee_tier_percentage;
          const icon0 = resolveTokenIcon(
            detail?.pool?.token0,
            position.pool?.token0_symbol,
          );
          const icon1 = resolveTokenIcon(
            detail?.pool?.token1,
            position.pool?.token1_symbol,
          );
          const positionValueUsd = formatFiatFromUsd(
            new BigNumber(position.position_value_usd ?? "0"),
            fiat,
          );
          const canWithdrawInline =
            flow.canWithdrawInline &&
            !!detail?.pool?.token0?.address &&
            !!detail?.pool?.token1?.address;

          return (
            <article
              className="wp-item"
              data-side="crossd"
              key={position.token_id}
            >
              <div className="wp-item-body">
                <div className="wp-item-line">
                  <span className="wp-item-label">Pool</span>
                  <div className="wp-svc-token">
                    <span className="wp-token-stack" aria-hidden>
                      <img className="wp-svc-token-icon" src={icon0} alt="" />
                      <img className="wp-svc-token-icon" src={icon1} alt="" />
                    </span>
                    <span>{pairLabel}</span>
                    {feeBadge && (
                      <span className="wp-token-subtle">{feeBadge}</span>
                    )}
                  </div>
                </div>
                <div className="wp-item-line">
                  <span className="wp-item-label">Position Value</span>
                  <span className="wp-line-value">{positionValueUsd}</span>
                </div>
              </div>
              <div className="wp-item-tags">
                {canWithdrawInline ? (
                  <button
                    type="button"
                    className="wp-tag wp-tag--muted"
                    data-track="crossd_pool_withdraw_open"
                    data-track-id={pairLabel}
                    onClick={() => {
                      if (!detail) return;
                      flow.openWithdraw({ position, detail });
                    }}
                  >
                    <span className="wp-tag-label">Withdraw</span>
                  </button>
                ) : (
                  <a
                    className="wp-tag wp-tag--muted"
                    href={poolWebUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-track="crossd_pool_outlink"
                    data-track-id={pairLabel}
                    onClick={(e) =>
                      handleOutlinkClick(
                        e,
                        poolWebUrl,
                        {
                          category: "portfolio",
                          origin: "portfolio-crossd-pool",
                          payload: { position, detail },
                        },
                        onOutlink,
                      )
                    }
                  >
                    <span className="wp-tag-label">Withdraw</span>
                    <ExternalLinkIcon size={12} className="wp-tag-icon" />
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </SectionCard>

      <CrossdWithdrawDialog
        open={flow.isOpen}
        status={flow.status}
        target={flow.target}
        token0Icon={targetIcon0}
        token1Icon={targetIcon1}
        token0Symbol={targetSymbol0}
        token1Symbol={targetSymbol1}
        nativeSymbol={getNativeToken()?.symbol}
        txHash={flow.txHash}
        error={flow.error}
        copied={flow.copied}
        hasEstimator={flow.hasEstimator}
        prepareWithdraw={flow.prepareWithdraw}
        confirmWithdraw={flow.confirmWithdraw}
        onClose={flow.closeWithdraw}
        onCopyHash={flow.copyHash}
        trackFunnel={flow.trackFunnelOnce}
      />
    </>
  );
}
